import type { ReportSupplementEntry } from "../../types/reportTemplate";
import { resolveRichTextForDisplay } from "../../utils/helpers";

export const MAX_ADDENDUM_FRAGMENT_CHARACTERS = 1200;
export const MAX_ADDENDUM_FRAGMENT_BLOCKS = 12;

export interface AddendumReportFragment {
  addendumIndex: number;
  fragmentIndex: number;
  contentHtml: string;
  textContent: string;
  showHeading: boolean;
  showSignature: boolean;
  photoRowIndex?: number;
}

export interface RichTextReportFragment {
  contentHtml: string;
  textContent: string;
}

interface RichTextReportFragmentOptions {
  splitAtEveryBreakpoint?: boolean;
}

interface RichTextBlock {
  html: string;
  text: string;
  semanticUnits: number;
}

interface IndexedTextNode {
  node: Node;
  startOffset: number;
  endOffset: number;
}

export function buildAddendumReportFragments(
  addendums: ReportSupplementEntry[]
): AddendumReportFragment[] {
  return addendums.flatMap((addendum, addendumIndex) => {
    const contentHtml = resolveRichTextForDisplay(
      addendum.contentHtml || addendum.comments || "",
      {}
    );
    const contentBlocks = buildRichTextReportFragments(contentHtml);

    const photoRowCount = Math.ceil((addendum.reportPhotoIds?.length || 0) / 3);
    return [
      ...contentBlocks.map((block, fragmentIndex) => ({
        addendumIndex,
        fragmentIndex,
        contentHtml: block.contentHtml,
        textContent: block.textContent,
        showHeading: fragmentIndex === 0,
        showSignature:
          photoRowCount === 0 && fragmentIndex === contentBlocks.length - 1,
      })),
      ...Array.from({ length: photoRowCount }, (_, photoRowIndex) => ({
        addendumIndex,
        fragmentIndex: contentBlocks.length + photoRowIndex,
        contentHtml: "",
        textContent: "",
        showHeading: false,
        showSignature: photoRowIndex === photoRowCount - 1,
        photoRowIndex,
      })),
    ];
  });
}

export function buildRichTextReportFragments(
  contentHtml: string,
  options: RichTextReportFragmentOptions = {}
): RichTextReportFragment[] {
  const blocks = splitRichTextIntoBlocks(contentHtml);
  const contentBlocks = options.splitAtEveryBreakpoint
    ? blocks
    : selectPagePackingBlocks(contentHtml, blocks);
  return contentBlocks.map((block, fragmentIndex) => ({
    contentHtml:
      fragmentIndex === 0 ? block.html : removeDefaultLeadingMargin(block.html),
    textContent: block.text,
  }));
}

export function exceedsReportFragmentPackingLimits(
  semanticUnits: number,
  textLength: number
): boolean {
  return (
    semanticUnits > MAX_ADDENDUM_FRAGMENT_BLOCKS ||
    textLength > MAX_ADDENDUM_FRAGMENT_CHARACTERS
  );
}

function removeDefaultLeadingMargin(contentHtml: string): string {
  const element = parseElement(contentHtml);
  if (
    element.style.margin ||
    element.style.marginTop ||
    element.style.marginBlockStart
  ) {
    return contentHtml;
  }
  element.style.marginTop = "0px";
  element.style.marginBlockStart = "0px";
  return element.outerHTML;
}

function selectPagePackingBlocks(
  originalHtml: string,
  blocks: RichTextBlock[]
): RichTextBlock[] {
  if (blocks.length === 0) {
    return [{ html: "", text: "", semanticUnits: 0 }];
  }
  const totalTextLength = blocks.reduce(
    (length, block) => length + block.text.length,
    0
  );
  const totalSemanticUnits = blocks.reduce(
    (count, block) => count + block.semanticUnits,
    0
  );
  if (
    !exceedsReportFragmentPackingLimits(totalSemanticUnits, totalTextLength)
  ) {
    return [toOriginalRichTextBlock(originalHtml, totalSemanticUnits)];
  }

  return blocks;
}

function toOriginalRichTextBlock(
  contentHtml: string,
  semanticUnits: number
): RichTextBlock {
  const template = document.createElement("template");
  template.innerHTML = contentHtml;
  const text = Array.from(template.content.childNodes)
    .map((node) => (node.textContent || "").trim())
    .filter(Boolean)
    .join(" ");
  return { html: contentHtml, text, semanticUnits };
}

function splitRichTextIntoBlocks(contentHtml: string): RichTextBlock[] {
  if (!contentHtml.trim()) return [];

  const template = document.createElement("template");
  template.innerHTML = contentHtml;

  return Array.from(template.content.childNodes).flatMap((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      if (!(node.textContent || "").trim()) return [];
      return splitElementAtWordBoundaries(wrapTextNode(node));
    }

    if (!(node instanceof HTMLElement)) return [];
    if (node.matches("ol, ul")) return splitList(node);
    if (node.querySelector("br")) {
      return splitElementAtHardBreaks(node);
    }
    return splitElementAtWordBoundaries(node);
  });
}

function wrapTextNode(node: Node): HTMLElement {
  const paragraph = document.createElement("p");
  paragraph.textContent = node.textContent || "";
  return paragraph;
}

function splitList(list: HTMLElement): RichTextBlock[] {
  const listItems = Array.from(list.children).filter(
    (child): child is HTMLLIElement => child instanceof HTMLLIElement
  );
  if (listItems.length === 0) return splitElementAtWordBoundaries(list);
  return buildListItemFragments(list, listItems).map((itemFragment) =>
    createListItemBlock(list, itemFragment)
  );
}

function createListItemBlock(
  list: HTMLElement,
  itemFragment: ListItemFragment
): RichTextBlock {
  const listClone = list.cloneNode(false) as HTMLElement;
  if (
    listClone instanceof HTMLOListElement &&
    itemFragment.ordinal !== undefined
  ) {
    listClone.start = itemFragment.ordinal;
  }
  listClone.appendChild(itemFragment.element);
  return toRichTextBlock(listClone);
}

interface ListItemFragment {
  element: HTMLLIElement;
  ordinal?: number;
  text: string;
}

function buildListItemFragments(
  list: HTMLElement,
  listItems: HTMLLIElement[]
): ListItemFragment[] {
  let ordinal =
    list instanceof HTMLOListElement
      ? list.hasAttribute("start")
        ? list.start
        : list.reversed
        ? listItems.length
        : 1
      : undefined;

  return listItems.flatMap((listItem) => {
    const explicitValue = listItem.getAttribute("value");
    if (explicitValue !== null) ordinal = Number(explicitValue);
    const itemOrdinal = ordinal;
    if (ordinal !== undefined)
      ordinal += list.hasAttribute("reversed") ? -1 : 1;

    return splitElementAtWordBoundaries(listItem).map(
      (itemBlock, itemBlockIndex) => {
        const element = parseElement(itemBlock.html);
        if (!(element instanceof HTMLLIElement)) {
          throw new Error("Expected a list item while paginating an addendum");
        }
        if (itemBlockIndex > 0) {
          element.style.listStyleType = "none";
          if (itemOrdinal !== undefined) element.value = itemOrdinal;
        }
        return {
          element,
          ordinal: itemOrdinal,
          text: itemBlock.text,
        };
      }
    );
  });
}

function splitElementAtHardBreaks(element: HTMLElement): RichTextBlock[] {
  const hardBreaks = Array.from(element.querySelectorAll("br"));
  const boundaryIndexes = hardBreaks.map((_hardBreak, index) => index);
  const chunks = boundaryIndexes.map((boundaryIndex, index) =>
    cloneElementBetweenHardBreaks(
      element,
      index === 0 ? null : boundaryIndexes[index - 1],
      boundaryIndex
    )
  );
  chunks.push(
    cloneElementBetweenHardBreaks(
      element,
      boundaryIndexes[boundaryIndexes.length - 1] ?? null,
      null
    )
  );

  return chunks.flatMap(splitHardBreakChunk);
}

function splitHardBreakChunk(chunk: HTMLElement): RichTextBlock[] {
  const splitByLength = splitElementAtWordBoundaries(chunk);
  if (splitByLength.length > 1) return splitByLength;
  return [toRichTextBlock(chunk)];
}

function cloneElementBetweenHardBreaks(
  element: HTMLElement,
  startAfterIndex: number | null,
  endBeforeIndex: number | null
): HTMLElement {
  const clone = element.cloneNode(true) as HTMLElement;
  const clonedBreaks = Array.from(clone.querySelectorAll("br"));
  const startAfter =
    startAfterIndex === null ? null : clonedBreaks[startAfterIndex];
  const endBefore =
    endBeforeIndex === null ? null : clonedBreaks[endBeforeIndex];

  if (endBefore) {
    const trailingRange = document.createRange();
    trailingRange.setStartBefore(endBefore);
    trailingRange.setEnd(clone, clone.childNodes.length);
    trailingRange.deleteContents();
  }
  if (startAfter) {
    const leadingRange = document.createRange();
    leadingRange.setStart(clone, 0);
    leadingRange.setEndAfter(startAfter);
    leadingRange.deleteContents();
  }
  return clone;
}

function splitElementAtWordBoundaries(element: HTMLElement): RichTextBlock[] {
  const text = element.textContent || "";
  if (text.length <= MAX_ADDENDUM_FRAGMENT_CHARACTERS) {
    return [toRichTextBlock(element)];
  }

  const ranges = findTextRanges(text, MAX_ADDENDUM_FRAGMENT_CHARACTERS);
  const textNodeIndex = buildTextNodeIndex(element);
  return ranges.map(({ start, end }) => {
    const clone = element.cloneNode(false) as HTMLElement;
    const range = document.createRange();
    const startPosition = findTextPosition(element, textNodeIndex, start);
    const endPosition = findTextPosition(element, textNodeIndex, end);
    range.setStart(startPosition.node, startPosition.offset);
    range.setEnd(endPosition.node, endPosition.offset);
    clone.appendChild(range.cloneContents());
    return toRichTextBlock(clone);
  });
}

function findTextRanges(
  text: string,
  maximumLength: number
): Array<{ start: number; end: number }> {
  const ranges: Array<{ start: number; end: number }> = [];
  let start = 0;

  while (start < text.length) {
    const hardEnd = Math.min(start + maximumLength, text.length);
    if (hardEnd === text.length) {
      ranges.push({ start, end: hardEnd });
      break;
    }
    const lastWhitespace = text.lastIndexOf(" ", hardEnd);
    const end = lastWhitespace > start ? lastWhitespace : hardEnd;
    let nextStart = end;
    while (nextStart < text.length && /\s/.test(text[nextStart])) {
      nextStart += 1;
    }
    ranges.push({ start, end: nextStart });
    start = nextStart;
  }

  return ranges;
}

function buildTextNodeIndex(root: HTMLElement): IndexedTextNode[] {
  const index: IndexedTextNode[] = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let traversed = 0;
  let current = walker.nextNode();

  while (current) {
    const length = current.textContent?.length ?? 0;
    index.push({
      node: current,
      startOffset: traversed,
      endOffset: traversed + length,
    });
    traversed += length;
    current = walker.nextNode();
  }

  return index;
}

function findTextPosition(
  root: HTMLElement,
  textNodeIndex: IndexedTextNode[],
  characterOffset: number
): { node: Node; offset: number } {
  const indexedNode = textNodeIndex.find(
    (candidate) => candidate.endOffset >= characterOffset
  );
  if (indexedNode)
    return {
      node: indexedNode.node,
      offset: characterOffset - indexedNode.startOffset,
    };

  return { node: root, offset: root.childNodes.length };
}

function parseElement(html: string): HTMLElement {
  const template = document.createElement("template");
  template.innerHTML = html;
  const element = template.content.firstElementChild;
  if (!(element instanceof HTMLElement)) {
    throw new Error("Expected report rich text to contain an HTML element");
  }
  return element;
}

function toRichTextBlock(
  element: HTMLElement,
  semanticUnits = 1
): RichTextBlock {
  return {
    html: element.outerHTML,
    text: (element.textContent || "").trim(),
    semanticUnits,
  };
}
